import React, { useState, useEffect } from 'react'

import { Formik } from 'formik';
import * as yup from 'yup';
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom';

import Moment from 'moment';
import 'moment/locale/es';

import { uploadBalanceData, cleanUpUploadBalanceData } from '../../../store/balances/balances.actions';
import { getInitialBalanceTemplate } from '../../../store/balances/balances.actions';

// Components

import Loader from '../../UI/Loader/Loader';
import FileInput from '../../UI/FormInput/FileInput';
import Button from '../../UI/Button/Button';
import UploadBalanceAlert from '../../UI/UploadBalanceAlert/UploadBalanceAlert';
import DateInput from '../../UI/FormInput/DateInput';
import SelectInput from '../../UI/FormInput/SelectInput';
import CheckboxInput from '../../UI/FormInput/CheckboxInput';

// Data

import { UPLOAD_TYPES } from '../../../config/constants';

// Utilities

import { setupFormData, getMaxDate } from '../../../utilities/helper-functions';

// Styles

import './Styles.scss';

export default function UploadBalanceForm({clientId, balanceTemplateUrl, balanceInitialTemplateUrl, longestBalancesArray, setShowUploadBalanceModal, hasData}) {
  const [showUploadResults, setShowUploadResults] = useState(false);
  const getBalanceTemplate = useSelector((state) => state.balances.getInitialBalanceTemplate);
  const balanceUpload = useSelector((state) => state.balances.uploadBalanceData);
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();

  // Upload Balance
  
  const uploadBalance = async (values, actions) => {
    const balances = longestBalancesArray?.balances;
    let vals = { ...values }
    let monthRangeNames = [];

    // Handle months for recurrent uploads
    if (balances && vals.upload_type === 2) {
      balances.forEach(item => {
        monthRangeNames?.push(item.title);
      });
      vals.months = JSON.stringify(monthRangeNames);
    }

    // Format date for recurrent uploads (but not for mass uploads)
    if (vals.upload_type === 2 && !vals.is_mass_upload) {
      vals.start_date = Moment(vals.start_date).format('MM-yyyy');
    } else {
      delete vals.start_date;
    }

    // Add mass upload flag if enabled
    if (vals.upload_type === 2 && vals.is_mass_upload) {
      vals.is_mass_upload = true;
    } else {
      delete vals.is_mass_upload;
    }

    const data = setupFormData(vals);
    dispatch(uploadBalanceData(data));
  }

  useEffect(() => {
    dispatch(cleanUpUploadBalanceData());
    dispatch(getInitialBalanceTemplate());
  }, [dispatch])

  useEffect(() => {
    if (balanceUpload.isUploaded) {
      if (balanceUpload.data?.logs?.length > 0) {
        setShowUploadResults(true);
      } else {
        setShowUploadBalanceModal(false);
      }
      // dispatch(cleanUpUploadBalanceData());
    }
  }, [dispatch, setShowUploadBalanceModal, balanceUpload])

  return (
    <>
      {/* Solo mostrar loader si hay error en getBalanceTemplate, no por loading */}
      {getBalanceTemplate.error &&
      <Loader
          message={'Error cargando template...'}
          error={getBalanceTemplate.error}
          action={getInitialBalanceTemplate}
      />}

      {/* Mostrar formulario siempre, independientemente del estado de getBalanceTemplate */}
      {!showUploadResults &&
      <Formik
          initialValues={{
            isAdmin: user.data?.role === 1 ? true : false,
            upload_type: (user.data?.role === 1 && !hasData) ? 1 : 2,
            start_date: getMaxDate(),
            customer_id: clientId,
            balances: null,
            is_mass_upload: false,
          }}
          validationSchema={validationSchema}
          onSubmit={(values, actions) => uploadBalance(values, actions)}
        >

          {props => (
          <>
          
            {/* Show uploaded balance file errors or regular response error message */}
          
            {balanceUpload.error && (typeof balanceUpload.error === 'object') ? (

              <UploadBalanceAlert data={balanceUpload} />

            ) : balanceUpload.error && (typeof balanceUpload.error !== 'object') ? (
            
              <p className="form-error-message">{balanceUpload.error}</p>
            
            ): (null)}

            {/* Form Inputs */}
            
            <>

              {user.data?.role === 1 &&
              <SelectInput
                formik={props}
                fieldName={'upload_type'}
                label={'Tipo de carga'}
                placeholder={'Seleccione'}
                options={UPLOAD_TYPES}
                disabled={hasData}
              />}

              {props.values.upload_type === 2 &&
              <CheckboxInput
                formik={props}
                fieldName={'is_mass_upload'}
                label={'Habilitar carga masiva de archivos'}
              />}

              {(props.values.upload_type === 2 && !props.values.is_mass_upload) &&
              <DateInput
                formik={props}
                fieldName={'start_date'}
                label={'Fecha'}
              />}

              <FileInput
                formik={props}
                fieldName={'balances'}
                label={props.values.is_mass_upload ? 'Archivos de balance (múltiples)' : 'Archivo balance'}
                fileType={'excel'}
                multiple={props.values.is_mass_upload}
              />

              <div className='balance-template-download'>
                {props.values.is_mass_upload ? (
                  <>
                    <strong>Carga Masiva:</strong> Puede seleccionar múltiples archivos de balance para cargar varios períodos a la vez.
                    <br/><br/>
                    <strong>Formato de nombres:</strong> Los archivos deben nombrarse siguiendo el formato: <code>balanza-mes-año.xlsx</code> (ejemplo: <code>balanza-enero-2024.xlsx</code>)
                    <br/><br/>
                  </>
                ) : null}
                
                ¿No tiene el formato de archivo de su balanza?{' '}
                {balanceTemplateUrl &&
                <Link to={balanceTemplateUrl} className='balance-template-link' target="_blank" download>
                  Descarge el template
                </Link>}
                {' '}para subir los datos de sus cuentas.
                <br/><br/>

                {/* Inicial Balance Upload Template Link */}

                {user.data?.role === 1 &&
                <>
                  ¿Desea subir una carga inicial?{' '}
                  {getBalanceTemplate?.isLoading ? (
                    <span className='balance-template-link' style={{color: '#666'}}>Cargando template...</span>
                  ) : getBalanceTemplate?.data?.url ? (
                    <a href={getBalanceTemplate?.data?.url} className='balance-template-link' target="_blank" rel="noopener noreferrer">
                      Descarge el template de carga inicial
                    </a>
                  ) : getBalanceTemplate.error ? (
                    <span className='balance-template-link' style={{color: '#999'}}>Template no disponible</span>
                  ) : (
                    <span className='balance-template-link' style={{color: '#666'}}>Cargando...</span>
                  )}
                  {' '}para subir los datos.
                </>}
              </div>
            
              <div className='form-actions'>

                <Button
                  action={() => setShowUploadBalanceModal(false)}
                  label={'Cancelar'}
                  secondaryStyle={true}
                  disabled={balanceUpload?.isLoading}
                />

                <Button
                  action={props.handleSubmit}
                  label={'Enviar'}
                  disabled={balanceUpload?.isLoading}
                  isLoading={balanceUpload?.isLoading}
                />

              </div>
            </>

          </>)}

      </Formik>}

      {showUploadResults &&
      <div className='upload-balance-results'>
        <h2>Resultados</h2>
        <p>La balanza fue cargada exitosamente, sin embargo la misma presenta los siguientes issues:</p>

        <ul>
          {balanceUpload.data?.logs && balanceUpload.data?.logs.map((log, i) =>
            <li key={i}><span className='upload-balance-results-issue-number'>{i + 1}</span> <span className='upload-balance-results-issue-message'>{log.message}</span></li>
          )}
        </ul>

        <div className='upload-balance-results-actions'>
          <Button
            action={() => setShowUploadBalanceModal(false)}
            label={'Cerrar'}
          />
        </div>
      </div>
      }
    </>
  )
}

// Form initial values and validaiton schemas

const validationSchema = yup.object().shape({
  upload_type: yup.string().when('isAdmin', {
    is: (val) => val === true,
    then: yup.string().required('Este campo es requerido'),
    otherwise: yup.string(),
  }),
  start_date: yup.string().when(['upload_type', 'is_mass_upload'], {
    is: (upload_type, is_mass_upload) => upload_type === 2 && !is_mass_upload,
    then: yup.string().required('Este campo es requerido'),
    otherwise: yup.string(),
  }),
  balances: yup.mixed().when('is_mass_upload', {
    is: (val) => val === true,
    then: yup.array().min(1, 'Debe seleccionar al menos un archivo').required('Este campo es requerido'),
    otherwise: yup.mixed().required('Este campo es requerido'),
  }),
});